import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowLeft, ArrowRight, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BasicPackage = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const features = [
    { name: "2 Social Platforms Managed", included: true, description: "Expand your reach across multiple platforms" },
    { name: "12 Posts Per Month", included: true, description: "3 posts per week for consistent engagement" },
    { name: "Full Content Calendar", included: true, description: "Comprehensive monthly content strategy" },
    { name: "Monthly Analytics Report", included: true, description: "Detailed insights and recommendations" },
    { name: "Branded Graphics + Flyers", included: true, description: "Professional designs that match your brand" },
    { name: "Caption & Hashtag Strategy", included: true, description: "Optimized for maximum reach and engagement" },
    { name: "Limited Facebook Ads", included: true, description: "Basic ad campaigns to boost visibility" },
    { name: "Email Marketing Templates", included: true, description: "Ready-to-use email designs" },
    { name: "Video Editing", included: false },
    { name: "Priority Support", included: false }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Header onContactClick={scrollToContact} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-brand-olive/10 to-black"></div>
        <div className="absolute top-20 left-20 w-96 h-96 bg-brand-olive/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-brand-cream/70 hover:text-brand-yellow transition-colors duration-300 mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to All Packages
          </button>
          
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-yellow rounded-full mb-6">
              <Star className="w-5 h-5 text-brand-brown fill-brand-brown" />
              <span className="text-brand-brown font-bold">MOST POPULAR</span>
            </div>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-brand-olive/20 rounded-full border border-brand-olive/40 mb-6">
                <span className="text-brand-olive font-bold">BASIC PACKAGE</span>
              </div>
              <h1 className="text-6xl lg:text-7xl font-bold text-white mb-6">
                <span className="text-brand-olive">£150</span>/month
              </h1>
              <p className="text-2xl text-brand-cream/90 mb-8">
                For growing freelancers ready to scale their digital presence
              </p>
              <Button 
                onClick={scrollToContact}
                size="lg"
                className="bg-brand-olive hover:bg-brand-olive/90 text-white font-bold px-12 py-6 text-xl"
              >
                Get Started
              </Button>
            </div>
            
            <div className="bg-gradient-to-br from-brand-olive/90 to-brand-olive rounded-3xl p-10 border-4 border-brand-yellow shadow-2xl backdrop-blur-xl ring-4 ring-brand-yellow/20">
              <h3 className="text-2xl font-bold text-white mb-6">What's Included</h3>
              <ul className="space-y-4">
                {features.slice(0, 8).map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-brand-yellow flex-shrink-0 mt-1" />
                    <div>
                      <span className="text-white font-medium">{feature.name}</span>
                      {feature.description && (
                        <p className="text-white/80 text-sm mt-1">{feature.description}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Wave Transition */}
      <div className="relative">
        <svg className="w-full h-24" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="url(#gradient1)" />
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(165, 166, 117, 0.15)" />
              <stop offset="50%" stopColor="rgba(236, 203, 51, 0.15)" />
              <stop offset="100%" stopColor="rgba(165, 166, 117, 0.15)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Detailed Features */}
      <section className="py-20 px-6 bg-gradient-to-b from-brand-olive/10 to-black">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-white mb-12 text-center">Why Our Most Popular Choice?</h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gradient-to-br from-brand-olive/30 to-black/60 rounded-2xl p-8 border border-brand-olive/40">
              <h3 className="text-xl font-bold text-brand-yellow mb-4">Best Value</h3>
              <p className="text-brand-cream/80">Perfect balance of features and affordability for most growing businesses.</p>
            </div>
            <div className="bg-gradient-to-br from-brand-olive/30 to-black/60 rounded-2xl p-8 border border-brand-olive/40">
              <h3 className="text-xl font-bold text-brand-yellow mb-4">Multi-Platform</h3>
              <p className="text-brand-cream/80">Reach more customers with presence on 2 key social platforms.</p>
            </div>
            <div className="bg-gradient-to-br from-brand-olive/30 to-black/60 rounded-2xl p-8 border border-brand-olive/40">
              <h3 className="text-xl font-bold text-brand-yellow mb-4">Ad Support</h3>
              <p className="text-brand-cream/80">Start driving targeted traffic with included Facebook ad campaigns.</p>
            </div>
          </div>

          {/* Upgrade Path */}
          <div className="bg-gradient-to-r from-brand-yellow/20 to-brand-olive/20 rounded-2xl p-8 border border-brand-yellow/40 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">Ready for More?</h3>
            <p className="text-brand-cream/90 mb-6">Upgrade to Advanced for full content creation and priority support</p>
            <Button
              onClick={() => navigate('/packages/advanced')}
              className="bg-brand-yellow hover:bg-brand-yellow/90 text-brand-brown font-bold"
            >
              Explore Advanced Package
            </Button>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-20">
            <button
              onClick={() => navigate('/packages/starter')}
              className="flex items-center gap-2 text-brand-yellow hover:text-brand-yellow/80 transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              Previous: Starter Package
            </button>
            <button
              onClick={() => navigate('/packages/advanced')}
              className="flex items-center gap-2 text-brand-yellow hover:text-brand-yellow/80 transition-colors duration-300"
            >
              Next: Advanced Package
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Wave Transition */}
      <div className="relative">
        <svg className="w-full h-24 rotate-180" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="url(#gradient2)" />
          <defs>
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(165, 166, 117, 0.15)" />
              <stop offset="50%" stopColor="rgba(236, 203, 51, 0.15)" />
              <stop offset="100%" stopColor="rgba(165, 166, 117, 0.15)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <Footer />
    </div>
  );
};

export default BasicPackage;
